import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { Box, Button, Stack, Typography } from "@mui/material";
import { CALIBRATE_FONT_FAMILY_MONO } from "components/hoc/Theme";
import { useState } from "react";
import type {
  ConfigDiffEntry,
  DiffKind,
  RowChange,
  TableDiffEntry,
} from "./configDiff";

/**
 * Presentation of configDiff entries for the Review & Save dialog: field
 * labels with old -> new values in tinted monospace chips (old red, new
 * green), +/- item lists, and per-row table blocks. Long values elide and
 * expand on click.
 */

export type ChipTone = "old" | "new" | "neutral";

const EMPTY_GLYPH = "—"; // same em-dash placeholder as PayloadView

const TONE_SX: Record<ChipTone, Record<string, string>> = {
  old: {
    bgcolor: "rgba(211,47,47,0.06)",
    borderColor: "rgba(211,47,47,0.4)",
    color: "error.dark",
  },
  new: {
    bgcolor: "rgba(46,125,50,0.08)",
    borderColor: "rgba(46,125,50,0.4)",
    color: "success.dark",
  },
  neutral: {
    bgcolor: "background.paper",
    borderColor: "divider",
    color: "text.primary",
  },
};

const ELIDE_AT = 48;

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === "")
    return EMPTY_GLYPH;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return JSON.stringify(value) ?? EMPTY_GLYPH;
}

/** Monospace value chip; truncated values expand/collapse on click. */
export function ValueChip({
  value,
  tone = "neutral",
}: {
  value: unknown;
  tone?: ChipTone;
}) {
  const [expanded, setExpanded] = useState(false);
  const isComplex = typeof value === "object" && value !== null;
  const compact = displayValue(value);
  const full = isComplex ? JSON.stringify(value, null, 2) ?? compact : compact;
  const expandable = compact.length > ELIDE_AT;
  const shown = expanded
    ? full
    : expandable
      ? `${compact.slice(0, ELIDE_AT)}...`
      : compact;
  return (
    <Box
      component="span"
      onClick={expandable ? () => setExpanded((v) => !v) : undefined}
      title={
        expandable
          ? expanded
            ? "Hide full value"
            : "Show full value"
          : undefined
      }
      sx={{
        display: "inline-flex",
        alignItems: "center",
        maxWidth: "100%",
        border: "1px solid",
        borderRadius: 1,
        px: 1,
        py: 0.25,
        fontFamily: CALIBRATE_FONT_FAMILY_MONO,
        fontSize: 13,
        lineHeight: 1.5,
        ...TONE_SX[tone],
        ...(expandable ? { cursor: "pointer" } : null),
        ...(expanded
          ? { whiteSpace: "pre-wrap", wordBreak: "break-all" }
          : { wordBreak: "break-word" }),
      }}
    >
      {shown}
    </Box>
  );
}

const Arrow = () => (
  <ArrowRightAltIcon
    sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }}
  />
);

const KindTag = ({ kind }: { kind: DiffKind }) => (
  <Typography
    component="span"
    variant="caption"
    sx={{ color: kind === "added" ? "success.main" : "error.main" }}
  >
    ({kind})
  </Typography>
);

/** Label on the left, change content on the right (PayloadView row shape). */
function RowShell({
  label,
  labelWidth = 160,
  children,
}: {
  label: string;
  labelWidth?: number;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      <Typography
        variant="body2"
        color="text.secondary"
        fontWeight={600}
        sx={{ minWidth: labelWidth, pt: 0.25 }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 0.75,
          alignItems: "center",
          flexWrap: "wrap",
          minWidth: 0,
          flex: 1,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

/** old -> new (or one-sided added/removed) chip group. */
function ChangeChips({
  kind,
  oldValue,
  newValue,
}: {
  kind: DiffKind;
  oldValue?: unknown;
  newValue?: unknown;
}) {
  if (kind === "added") {
    return (
      <>
        <ValueChip value={newValue} tone="new" />
        <KindTag kind="added" />
      </>
    );
  }
  if (kind === "removed") {
    return (
      <>
        <ValueChip value={oldValue} tone="old" />
        <KindTag kind="removed" />
      </>
    );
  }
  return (
    <>
      <ValueChip value={oldValue} tone="old" />
      <Arrow />
      <ValueChip value={newValue} tone="new" />
    </>
  );
}

/** +/- item lines of a primitive-list diff. */
function ListItemsChange({
  added,
  removed,
  reordered,
}: {
  added: unknown[];
  removed: unknown[];
  reordered: boolean;
}) {
  const sign = (glyph: string, color: string) => (
    <Typography
      component="span"
      sx={{
        fontFamily: CALIBRATE_FONT_FAMILY_MONO,
        fontSize: 13,
        fontWeight: 700,
        color,
        width: 12,
        flexShrink: 0,
      }}
    >
      {glyph}
    </Typography>
  );
  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      {removed.map((item, i) => (
        <Box
          key={`r${i}`}
          sx={{ display: "flex", gap: 0.5, alignItems: "center" }}
        >
          {sign("-", "error.main")}
          <ValueChip value={item} tone="old" />
        </Box>
      ))}
      {added.map((item, i) => (
        <Box
          key={`a${i}`}
          sx={{ display: "flex", gap: 0.5, alignItems: "center" }}
        >
          {sign("+", "success.main")}
          <ValueChip value={item} tone="new" />
        </Box>
      ))}
      {reordered && (
        <Typography variant="caption" color="text.secondary">
          Items reordered
        </Typography>
      )}
    </Stack>
  );
}

/** One table row's block: identity caption + its (changed) cells. */
function RowBlock({ row }: { row: RowChange }) {
  return (
    <Box>
      <Typography
        variant="caption"
        fontWeight={700}
        display="block"
        sx={{
          color:
            row.kind === "added"
              ? "success.main"
              : row.kind === "removed"
                ? "error.main"
                : "text.secondary",
        }}
      >
        Row "{row.identity}"{row.kind !== "changed" && <> ({row.kind})</>}
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 0.25 }}>
        {row.cells.map((cell) => (
          <RowShell key={cell.key} label={cell.label} labelWidth={140}>
            <ChangeChips
              kind={row.kind}
              oldValue={cell.oldValue}
              newValue={cell.newValue}
            />
          </RowShell>
        ))}
      </Stack>
    </Box>
  );
}

const MAX_VISIBLE_ROWS = 8;

function TableChange({ entry }: { entry: TableDiffEntry }) {
  const [showAll, setShowAll] = useState(false);
  const rows = showAll ? entry.rows : entry.rows.slice(0, MAX_VISIBLE_ROWS);
  const hidden = entry.rows.length - rows.length;
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" fontWeight={600}>
        {entry.label}
      </Typography>
      <Stack
        spacing={1}
        sx={{
          mt: 0.5,
          pl: 1.5,
          borderLeft: "2px solid",
          borderColor: "divider",
        }}
      >
        {rows.map((row, i) => (
          <RowBlock key={`${row.identity}-${i}`} row={row} />
        ))}
        {hidden > 0 && (
          <Button
            size="small"
            onClick={() => setShowAll(true)}
            sx={{ alignSelf: "flex-start", textTransform: "none" }}
          >
            Show {hidden} more row{hidden === 1 ? "" : "s"}
          </Button>
        )}
        {entry.reordered && (
          <Typography variant="caption" color="text.secondary">
            Rows reordered
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

/** One diff entry, rendered by its shape. */
export function DiffEntryView({ entry }: { entry: ConfigDiffEntry }) {
  if (entry.type === "table") return <TableChange entry={entry} />;
  if (entry.type === "list") {
    return (
      <RowShell label={entry.label}>
        <ListItemsChange
          added={entry.added}
          removed={entry.removed}
          reordered={entry.reordered}
        />
      </RowShell>
    );
  }
  // scalar and opaque render the same way - ValueChip handles JSON values.
  return (
    <RowShell label={entry.label}>
      <ChangeChips
        kind={entry.kind}
        oldValue={entry.oldValue}
        newValue={entry.newValue}
      />
    </RowShell>
  );
}

/** Flat list of config diff entries. */
export default function ConfigDiffList({
  entries,
}: {
  entries: ConfigDiffEntry[];
}) {
  if (entries.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No visible field changes.
      </Typography>
    );
  }
  return (
    <Stack spacing={0.75}>
      {entries.map((entry) => (
        <DiffEntryView key={entry.path.join(".")} entry={entry} />
      ))}
    </Stack>
  );
}

/**
 * Scalar change row for non-config pending fields (Name, Enabled, cron...):
 * same chips/arrow presentation with pre-formatted display strings.
 */
export function ScalarChangeRow({
  label,
  oldText,
  newText,
}: {
  label: string;
  oldText: string;
  newText: string;
}) {
  return (
    <RowShell label={label}>
      <ValueChip value={oldText} tone="old" />
      <Arrow />
      <ValueChip value={newText} tone="new" />
    </RowShell>
  );
}
